import React, { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PDFDownloadButton = ({ 
  contentId, 
  fileName = 'document', 
  buttonText = 'Download PDF',
  className = '',
  variant = 'default'
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    
    try {
      const element = document.getElementById(contentId);
      if (!element) {
        console.error('Content element not found:', contentId);
        setIsGenerating(false);
        return;
      }

      // Store original state
      const scrollY = window.scrollY;
      
      // Hide elements we don't want
      const header = document.querySelector('header');
      const pdfBtn = document.querySelector('.pdf-download-btn');
      const excludeSections = document.querySelectorAll('.exclude-from-pdf');
      
      if (header) header.style.display = 'none';
      if (pdfBtn) pdfBtn.style.display = 'none';
      excludeSections.forEach(el => el.style.display = 'none');

      // Force all motion elements to be visible
      const allElements = element.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.style) {
          el.style.opacity = '1';
          el.style.visibility = 'visible';
          el.style.transform = 'none';
        }
      });

      // Scroll through page to load lazy content
      const totalHeight = element.scrollHeight;
      for (let y = 0; y < totalHeight; y += 500) {
        window.scrollTo(0, y);
        await new Promise(r => setTimeout(r, 50));
      }
      window.scrollTo(0, 0);
      await new Promise(r => setTimeout(r, 300));

      // Get sections to capture (excluding contact forms)
      const sections = Array.from(element.querySelectorAll('section:not(.exclude-from-pdf)'));
      const footer = element.querySelector('footer');
      
      const elementsToCapture = [...sections];
      if (footer) elementsToCapture.push(footer);
      
      console.log('Capturing', elementsToCapture.length, 'elements');

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 0; // No margins for cleaner look
      
      let currentY = 0;
      let isFirstElement = true;

      for (let i = 0; i < elementsToCapture.length; i++) {
        const el = elementsToCapture[i];
        
        // Force visibility
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        
        // Scroll into view
        el.scrollIntoView({ block: 'start', behavior: 'instant' });
        await new Promise(r => setTimeout(r, 150));
        
        console.log(`Capturing [${i+1}/${elementsToCapture.length}]: ${el.className}`);

        try {
          const canvas = await html2canvas(el, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,
            logging: false,
            windowWidth: 1200,
            onclone: (doc, clonedEl) => {
              clonedEl.style.opacity = '1';
              clonedEl.style.visibility = 'visible';
              clonedEl.style.transform = 'none';
              
              // Force visibility on all children
              const children = clonedEl.querySelectorAll('*');
              children.forEach(child => {
                if (child.style) {
                  child.style.opacity = '1';
                  child.style.visibility = 'visible';
                  child.style.transform = 'none';
                }
              });
            }
          });

          if (canvas.height < 10) continue;

          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          const scaledH = (canvas.height * pdfWidth) / canvas.width;
          
          // Check if we need a new page (section doesn't fit)
          if (!isFirstElement && currentY + scaledH > pdfHeight) {
            pdf.addPage();
            currentY = 0;
          }
          
          // Handle sections taller than one page
          if (scaledH > pdfHeight) {
            const numPages = Math.ceil(scaledH / pdfHeight);
            
            for (let p = 0; p < numPages; p++) {
              if (p > 0) {
                pdf.addPage();
                currentY = 0;
              }
              
              const srcY = Math.floor((p * pdfHeight * canvas.width) / pdfWidth);
              const srcH = Math.min(
                Math.floor((pdfHeight * canvas.width) / pdfWidth),
                canvas.height - srcY
              );
              
              if (srcH <= 0) break;
              
              const sliceCanvas = document.createElement('canvas');
              sliceCanvas.width = canvas.width;
              sliceCanvas.height = srcH;
              const ctx = sliceCanvas.getContext('2d');
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, srcH);
              ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
              
              const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.9);
              const sliceH = (srcH * pdfWidth) / canvas.width;
              
              pdf.addImage(sliceData, 'JPEG', margin, currentY, pdfWidth - (margin * 2), sliceH);
              currentY = sliceH;
            }
          } else {
            // Section fits on current page
            pdf.addImage(imgData, 'JPEG', margin, currentY, pdfWidth - (margin * 2), scaledH);
            currentY += scaledH;
          }
          
          isFirstElement = false;
          
        } catch (err) {
          console.error('Error capturing:', el.className, err);
        }
      }

      // Restore elements
      if (header) header.style.display = '';
      if (pdfBtn) pdfBtn.style.display = '';
      excludeSections.forEach(el => el.style.display = '');
      
      window.scrollTo(0, scrollY);

      // Save PDF
      pdf.save(`${fileName}.pdf`);
      console.log('PDF saved');
      
    } catch (error) {
      console.error('PDF error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const baseStyles = {
    default: 'pdf-btn-default',
    outline: 'pdf-btn-outline',
    minimal: 'pdf-btn-minimal',
  };

  return (
    <button
      onClick={generatePDF}
      disabled={isGenerating}
      className={`pdf-download-btn ${baseStyles[variant]} ${className}`}
      title="Download as PDF"
    >
      {isGenerating ? (
        <>
          <Loader2 size={16} className="spinning" />
          <span>Generating...</span>
        </>
      ) : (
        <>
          <FileDown size={16} />
          <span>{buttonText}</span>
        </>
      )}
    </button>
  );
};

export default PDFDownloadButton;
